
import {of as observableOf,  Observable } from 'rxjs';

import {map} from 'rxjs/operators';
import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { FlowTemplate, PublishInfo, EmailTemplate, StateType } from "../../common/models/flow.dto";
import { EntityMap } from "../../builder/models/entity.model";
import { ReportTemplate } from '../../models/comodel/mergefieldsmodel.model';
@Injectable()
export class FlowTemplateService {


    public static readonly baseUrl = '/api/flow/template';
    templateCache: Map<string, FlowTemplate>;
    emailTemplateCache: Array<EmailTemplate>;
    reportTemplateCache: Array<ReportTemplate>;
    constructor(public readonly http: HttpClient) {
        this.templateCache = new Map<string, FlowTemplate>();
    }

    get(id: string): Observable<FlowTemplate> {
        if (this.templateCache.has(id)) {
            let result = this.templateCache.get(id);
            return observableOf(result);
        }
        return this.http.get<FlowTemplate>(`${FlowTemplateService.baseUrl}/${id}`);
    }

    cacheStub(id: string): void {
        this.templateCache.set(id, this.makeStub(id));
    }

    getContextMap(templateId: string, majorVersion: number, minorVersion: number): Observable<any> {
        return this.http.get<any>(`/api/flow/template/contextMap/${templateId}/${majorVersion}/${minorVersion}`);
    }

    getByVersion(id: string, majorVersion: number, minorVersion: number, useCache: boolean): Observable<FlowTemplate> {
        //deliberately removing the ability to get from cache from getByVersion for now (2018-07-09) as it causes intermittent stack overflow issues with the diagram
        return this.http.get<FlowTemplate>(`${FlowTemplateService.baseUrl}/${id}/${majorVersion}.${minorVersion}`);
    }

    getList(): Observable<Array<FlowTemplate>> {
        return this.http.get<Array<FlowTemplate>>(`${FlowTemplateService.baseUrl}/getList`);
    }

    saveAsDraft(body: FlowTemplate): Observable<FlowTemplate> {
        this.templateCache.set(body.workflowId, body);
        return this.http.post<FlowTemplate>(`${FlowTemplateService.baseUrl}/${body.workflowId}`, body);
    }

    saveNew(moduleGroup: string, module: string, body: FlowTemplate): Observable<FlowTemplate> {
        this.templateCache.set(body.workflowId, body);
        return this.http.post<FlowTemplate>(`${FlowTemplateService.baseUrl}/${moduleGroup}/${module}/${body.workflowId}`, body);
    }

    publish(body: FlowTemplate): Observable<PublishInfo> {
        const publishInfo = {
            effectiveDate: body.effectiveDate,
            majorVersion: body.majorVersion,
            minorVersion: body.minorVersion,
            id: body.workflowId
        };
        return this.http.post<PublishInfo>(
            `${FlowTemplateService.baseUrl}/${body.workflowId}/${body.majorVersion}.${body.minorVersion}/publish`,
            publishInfo);
    }

    cacheExisting(): Observable<void> {
        return this.http.get<Array<FlowTemplate>>(`${FlowTemplateService.baseUrl}/getList`).pipe(map((result) => {
            result.forEach((r) => {
                const idParts = r.workflowId.split("_");
                if (!this.templateCache.has(idParts[0])) {
                    this.templateCache.set(idParts[0], r);
                }
            });
        }));
    }

    getVersionList(id: string): Observable<Array<FlowTemplate>> {
        const url = `${FlowTemplateService.baseUrl}/${id}/versionList`;
        return this.http.get<Array<FlowTemplate>>(url);
    }

    addToCache(addNew: boolean, id: string): Observable<void> {
        if (addNew || !this.templateCache.has(id)) {
            this.templateCache.set(id, this.makeStub(id));
        }
        return observableOf(null);
    }

    getAllEmailTemplates(): Observable<Array<EmailTemplate>> {
        if (this.emailTemplateCache !== undefined && this.emailTemplateCache !== null) {
            return observableOf(this.emailTemplateCache);
        }
        return this.http.get<Array<EmailTemplate>>(`${FlowTemplateService.baseUrl}/sideEffects/getEmailTemplates`).pipe(map((result) => {
            this.emailTemplateCache = result;
            return result;
        }));
    }

    getReportTemplates(): Observable<Array<ReportTemplate>> {
        if (this.reportTemplateCache) {
            return observableOf(this.reportTemplateCache);
        }
        return this.http.get<Array<ReportTemplate>>(`${FlowTemplateService.baseUrl}/sideEffects/getReportTemplates`).pipe(
            map((result) => {
                this.reportTemplateCache = result;
                return result;
            }));
    }

    importEntityMaps(file: any): Observable<EntityMap[]> {
        const formData = new FormData();
        formData.append("mapData", file);
        return this.http.post<EntityMap[]>("/api/flow/template/importEntityMap", formData);
    }

    importTemplate(file: any, importAsNewFlow: boolean, newFlowName: string, moduleGroupId: string, moduleId: string): Observable<any> {
        const formData = new FormData();
        formData.append("flowData", file);
        formData.append("moduleId", moduleId);
        formData.append("moduleGroupId", moduleGroupId);
        formData.append("newFlowName", newFlowName);
        formData.append("importAsNewFlow", importAsNewFlow ? "true" : "false");
        return this.http.post("/api/flow/template/import", formData);
    }


    public makeStub(id: string): FlowTemplate {
        return {
            id: `${id}_0.0`,
            workflowId: id,
            majorVersion: 0,
            minorVersion: 0,
            effectiveDate: new Date(),
            isPublished: false,
            isPublic: false,
            startAtSummary: true,
            name: "Example",
            entityMaps: [],
            context: {
                rules: []
            },
            states: [
                {
                    name: "Start",
                    title: "",
                    isInitial: true,
                    isTerminal: false,
                    transitions: [],
                    sections: [],
                    key: 1,
                    stateType: StateType.Start
                }
            ],
            model: {
                class: "go.GraphLinksModel",
                linkFromPortIdProperty: "fromPort",
                linkToPortIdProperty: "toPort",
                nodeDataArray: [
                    { "key": 1, "category": "start", "loc": "0 0", "text": "Start" }
                ],
                linkDataArray: [
                ]
            }
        };
    }



}
